import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Maximize2, X } from 'lucide-react';
import {
  ComposedChart,
  ReferenceArea,
  ReferenceLine,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { useFrameTime } from '@/components/ui/hover-time';
import { useT } from '@/contexts/I18nContext';
import { getQueueDetail, whDomain } from '@/api/analytics';
import {
  DayPhotoStrip,
  nearestPhotoIndex,
  type DayStripPhoto,
} from './DayPhotoStrip';
import { DayChartLightbox } from './DayChartLightbox';
import { MarqueeZoom } from '@/components/report/MarqueeZoom';
import { fmtDayTime, fmtTimeTick, makeTimeAxis, spansDays } from './timeAxis';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
// The chart's plot-area insets: YAxis width + margins (see renderChart). The
// photo strip and the lightbox marquee both align to these.
const PLOT_INSET = { left: 32, right: 8, bottom: 52 };

// 5-minute buckets for the day-long queue bar series — 15-min bars blurred
// the day into a single blob; the owner asked for a denser column rhythm.
const FIVE_MIN_MS = 5 * 60 * 1000;
const CHART_H = 200;
// Queue frames are wide (≈16:9); a wider slot keeps them readable inside the
// fixed gap-separated cells.
const QUEUE_SLOT_W = 120;

/** A request from a "Посмотреть" button in the report to open this card's
 *  fullscreen chart zoomed to a problem window. `nonce` re-fires repeat clicks. */
export interface QueueFocusRequest {
  domain: [number, number];
  nonce: number;
}

function clampDomain(
  zoom: [number, number],
  bounds: [number, number] | undefined,
): [number, number] {
  if (!bounds) return zoom;
  return [Math.max(zoom[0], bounds[0]), Math.min(zoom[1], bounds[1])];
}

function fmtClock(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Map queue-detail photos to the shared strip shape, marking INCIDENT frames
 *  (long queues) with the "interesting frame" orange marker: a queue only
 *  counts as notable if it clears BOTH ≥60% of the day's peak AND an absolute
 *  floor of NOTABLE_MIN people, capped at 8. A calm day (peak ~3) therefore
 *  marks NOTHING rather than orange-flagging the biggest handful of tiny
 *  queues — owner: "очередь 3 человека это мало, не подсвечивать". (Was: the
 *  top-3 biggest were ALWAYS marked regardless of size, floor only 3.) */
const NOTABLE_MIN = 5;
function buildQueueStrip(
  photos: Array<{ image_url: string | null; at: string; captured_occupancy?: number | null }>,
): DayStripPhoto[] {
  const ranked = [...photos]
    .filter((p) => p.image_url && (p.captured_occupancy ?? 0) > 0)
    .sort((a, b) => (b.captured_occupancy ?? 0) - (a.captured_occupancy ?? 0));
  const peakBar = Math.max(NOTABLE_MIN, (ranked[0]?.captured_occupancy ?? 0) * 0.6);
  const peakAts = new Set(
    ranked
      .filter((p) => (p.captured_occupancy ?? 0) >= peakBar)
      .slice(0, 8)
      .map((p) => p.at),
  );
  return photos.map((p) => ({
    image_url: p.image_url,
    at: p.at,
    metric: p.captured_occupancy != null ? `${p.captured_occupancy} чел` : null,
    dip: peakAts.has(p.at),
  }));
}

/**
 * Queue card for the "День" report (and, with `to`, the week's full-range
 * queue chart).
 *  - images ABOVE the chart, time-aligned along X on desktop (grouped on mobile);
 *  - one timeseries below: queue length as short vertical bars, with the number
 *    of open cashiers / kitchen staff drawn as calm accent lines behind them;
 *  - chart X domain = working hours (single day) or the data extent (range);
 *    click a bar → open the nearest photo.
 *
 * `bucketMs` overrides the series resolution (default 5-min). The report-validation
 * consult view passes a smaller bucket; the week view passes a larger one.
 */
export function DayQueueCard({
  zoneId,
  day,
  to,
  bucketMs = FIVE_MIN_MS,
  title,
  focusRequest,
  onFocusHandled,
  compact,
  zoom,
  onZoom,
}: {
  zoneId: string;
  day: string;
  /** Range end (YYYY-MM-DD); defaults to `day` (single-day card). */
  to?: string;
  bucketMs?: number;
  /** Card title override (the week view names it differently). */
  title?: string;
  focusRequest?: QueueFocusRequest | null;
  onFocusHandled?: () => void;
  /** Shrinks header padding + chart height (validation page consult column). */
  compact?: boolean;
  /** Controlled sub-period (analytics page): the marquee sets the PAGE period via
   *  onZoom — shown in the header picker, like «Выкладка» — instead of a local pill
   *  (Alexey 13.10 #5805: «период прямо в график ложится с крестиком» — путаница). */
  zoom?: [number, number] | null;
  onZoom?: (w: [number, number] | null) => void;
}) {
  const { t } = useT();
  const ft = useFrameTime();
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Preset zoom window for the fullscreen chart when opened from a "Посмотреть"
  // button; null → the lightbox opens at the full day.
  const [presetZoom, setPresetZoom] = useState<[number, number] | null>(null);
  // A "Посмотреть" button on the queue line asked to open the fullscreen chart
  // zoomed to the peak window. Fire once per click (keyed by nonce).
  useEffect(() => {
    if (!focusRequest) return;
    setPresetZoom(focusRequest.domain);
    setExpanded(true);
    onFocusHandled?.();
    // Intentionally keyed only on the click nonce (exhaustive-deps rule is not
    // enabled in this project; see reference_fe_test_harness).
  }, [focusRequest?.nonce]);

  // FIXED count so the SWR key never changes as the container width settles on
  // load — a width-derived count refetched a different picked set and the photos
  // flickered in/out (Alexey 2026-07-21). The strip renders as many as fit.
  //
  // Sized to ~2× a typical strip capacity (NOT 40): peak selection's min-gap is
  // span/(count·2), so at count=40 the gap collapses to ~9 min and the picker
  // returns ~one-local-max-per-bin — a near-UNIFORM spread that reads as "evenly
  // spaced, not at peaks" (Alexey 2026-07-22, 825b). At ~16 the gap is ~22 min,
  // so the pool is the day's genuinely separated peaks; the strip then shows a
  // subset of THOSE, so whatever it renders is a real peak. Display count is
  // unchanged (it's still capacity, which is smaller than this).
  const photoCount = 16;

  const toDay = to ?? day;

  const { data, isLoading } = useSWR(
    zoneId ? ['report:day-queue', zoneId, day, toDay, photoCount, bucketMs] : null,
    () =>
      getQueueDetail(zoneId, day, toDay, bucketMs, {
        photoMode: 'peak',
        photoCount,
      }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  const overlay = data?.overlay ?? [];
  const photos = data?.queue_photos ?? [];
  const wh = data?.working_hours ?? null;
  // Приход сотрудников (#1324): полоса открытия, пунктир назначенного времени, палочка первого
  // появления; линия «за баром» показана по умолчанию и тянется до открытия.
  const sa = data?.staff_arrival ?? null;
  const saTs = useMemo(() => {
    if (!sa) return null;
    const n = (iso: string | null) => (iso ? new Date(iso).getTime() : NaN);
    return { open: n(sa.open_at), expected: n(sa.expected_at), captureFrom: n(sa.capture_from), first: n(sa.first_seen_at) };
  }, [sa]);
  const nobodyBeforeOpen = !!sa && saTs != null && (sa.status === 'nobody' || (Number.isFinite(saTs.first) && saTs.first >= saTs.open));

  const hasCashier = useMemo(
    () => overlay.some((p) => (p.cashier_count ?? 0) > 0),
    [overlay],
  );
  const hasKitchen = useMemo(
    () => overlay.some((p) => (p.kitchen ?? 0) > 0),
    [overlay],
  );

  // Legend-click visibility. «Открытых касс» and «За баром» are MUTUALLY
  // EXCLUSIVE (all three series at once read as noise): clicking the hidden
  // one swaps them, clicking the visible one just hides it. Cashiers win by
  // default. The queue bars toggle independently.
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({ kitchen: true });
  // С включённым «Открытием смены» по умолчанию видна линия «за баром», а не касс (#1324).
  const saDefaultApplied = useRef(false);
  useEffect(() => {
    if (sa && !saDefaultApplied.current) {
      saDefaultApplied.current = true;
      setHiddenSeries({ cashier_count: true });
    }
  }, [sa]);
  const toggleSeries = (dataKey: string) =>
    setHiddenSeries((h) => {
      if (dataKey === 'kitchen' || dataKey === 'cashier_count') {
        const other = dataKey === 'kitchen' ? 'cashier_count' : 'kitchen';
        return h[dataKey]
          ? { ...h, [dataKey]: false, [other]: true } // show me, hide the other
          : { ...h, [dataKey]: true };
      }
      return { ...h, [dataKey]: !h[dataKey] };
    });

  // Numeric (time) X axis so the domain can be pinned to working hours and the
  // images above line up with the bars by real time. BREAK rows are injected
  // into capture gaps (nights / outages) so the cashier/kitchen lines stop
  // there instead of drawing a fake constant line across the whole night.
  const withGapBreaks = useCallback(
    (rows: typeof overlay) => {
      const out: Array<Partial<(typeof overlay)[number]> & { ts: number; time: string }> = [];
      let prevTs: number | null = null;
      for (const p of rows) {
        const ts = new Date(p.time).getTime();
        if (prevTs != null && ts - prevTs > bucketMs * 3) {
          out.push({ ts: prevTs + bucketMs, time: new Date(prevTs + bucketMs).toISOString() });
        }
        out.push({ ...p, ts });
        prevTs = ts;
      }
      return out;
    },
    [bucketMs],
  );
  const chartData = useMemo(() => withGapBreaks(overlay), [overlay, withGapBreaks]);

  // Fullscreen zoom → FINER buckets: the marquee window refetches the overlay
  // at a resolution matched to the span, instead of stretching the day-level
  // bars. The day-level data stays as the instant placeholder.
  const [lbZoom, setLbZoom] = useState<[number, number] | null>(null);
  // Зум прямо на карточке (Алексей 12.10, #1431/#1435): та же MarqueeZoom, что на
  // вкладке «Выкладка» — тянем окно, двойной тап или «вся смена» — сброс. Окно
  // кормит тот же запрос мелких бакетов, что и полноэкранный зум (lbZoom).
  const [cardZoom, setCardZoom] = useState<[number, number] | null>(null);
  const controlled = onZoom != null;
  useEffect(() => {
    if (!controlled) return;
    setCardZoom(zoom ?? null);
    setLbZoom(zoom ?? null);
  }, [controlled, zoom?.[0], zoom?.[1]]);
  const zoomBucketMs = useMemo(() => {
    if (!lbZoom) return null;
    const span = lbZoom[1] - lbZoom[0];
    const fine = span <= 2 * 3600_000 ? 60_000 : span <= 8 * 3600_000 ? 2 * 60_000 : null;
    return fine && fine < bucketMs ? fine : null;
  }, [lbZoom, bucketMs]);
  const { data: zoomData } = useSWR(
    zoneId && (expanded || cardZoom) && lbZoom && zoomBucketMs
      ? ['report:day-queue-zoom', zoneId, day, toDay, lbZoom[0], lbZoom[1], zoomBucketMs]
      : null,
    () =>
      getQueueDetail(zoneId, day, toDay, zoomBucketMs!, {
        photoMode: 'peak',
        photoCount: 4,
        fromTs: new Date(lbZoom![0]).toISOString(),
        toTs: new Date(lbZoom![1]).toISOString(),
      }).then((r) => r.data),
    { revalidateOnFocus: false, keepPreviousData: true },
  );
  const zoomRows = useMemo(
    () => withGapBreaks(zoomData?.overlay ?? []),
    [zoomData, withGapBreaks],
  );
  const rowsFor = (dom: [number, number]) =>
    lbZoom && zoomRows.length > 1 && dom[0] === lbZoom[0] && dom[1] === lbZoom[1]
      ? zoomRows
      : chartData;

  // A drag on the in-card chart is a failed marquee attempt, not a click —
  // the guard suppresses the click-to-open-photo handler after real drags.
  const clickGuard = useRef(false);
  const guardStart = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    clickGuard.current = false;
  }, [expanded]);
  const domain = useMemo<[number, number] | undefined>(() => {
    if (wh) {
      // Padded VISUAL domain (chart X + strip axis derive from this); timing
      // reads wh.start directly (888).
      const d = whDomain(wh);
      let s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      // Съёмка ради прихода начинается раньше открытия — показываем и её (#1324).
      if (saTs && Number.isFinite(saTs.captureFrom)) s = Math.min(s, saTs.captureFrom - 5 * 60_000);
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    // Multi-day ranges have no single working-hours window — pin the domain
    // to the data extent instead.
    if (chartData.length > 1) {
      const s = chartData[0].ts;
      const e = chartData[chartData.length - 1].ts + bucketMs;
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return undefined;
  }, [wh, chartData, bucketMs, saTs]);
  // Домен карточки: окно зума, если выделили, иначе вся смена.
  const cardDomain = useMemo<[number, number] | undefined>(
    () => (cardZoom && domain ? [Math.max(cardZoom[0], domain[0]), Math.min(cardZoom[1], domain[1])] : domain),
    [cardZoom, domain],
  );

  // Pin the Y max to the data (visible series only) instead of letting
  // recharts pick a rounded "nice" ceiling — a max-5 day was rendering an
  // 8-tall axis with a big empty band on top. +1 unit of headroom keeps the
  // tallest bar off the very top edge.
  const yMax = useMemo(() => {
    let m = 0;
    for (const p of chartData) {
      if (!hiddenSeries.queue) m = Math.max(m, p.queue ?? 0);
      if (hasCashier && !hiddenSeries.cashier_count) m = Math.max(m, p.cashier_count ?? 0);
      if (hasKitchen && !hiddenSeries.kitchen) m = Math.max(m, p.kitchen ?? 0);
    }
    // Порог очереди тоже должен быть виден, даже если за день до него не доходило.
    for (const l of data?.queue_limits ?? []) if (!hiddenSeries.queue) m = Math.max(m, l.max_allowed);
    return Math.max(1, Math.ceil(m) + 1);
  }, [chartData, hasCashier, hasKitchen, hiddenSeries, data?.queue_limits]);

  // Venues with 2+ named queues: split the bars into stacked SHADES of the
  // queue orange (not new colours — the chart stays calm), split the tooltip
  // and the photo captions per queue.
  const queues = data?.queues ?? [];
  const multiQueue = queues.length >= 2;
  const QUEUE_SHADES = ['#f59e0b', '#fbbf24', '#b45309', '#fde68a'];

  // Per-queue counts at a photo's moment (nearest overlay bucket).
  const queueSplitAt = useCallback(
    (iso: string): string => {
      if (!multiQueue || !chartData.length) return '';
      const t = new Date(iso).getTime();
      if (!Number.isFinite(t)) return '';
      let best: (typeof chartData)[number] | null = null;
      let bestD = Infinity;
      for (const row of chartData) {
        const d = Math.abs(row.ts - t);
        if (d < bestD) {
          bestD = d;
          best = row;
        }
      }
      if (!best) return '';
      return queues
        .map((q) => `${q.name}: ${(best as Record<string, unknown>)[`queue_${q.id}`] ?? 0}`)
        .join(' · ');
    },
    [multiQueue, chartData, queues],
  );

  // Кадр первого появления сотрудников — в ленту и по клику у отметки прихода (Алексей 12.10, #1431).
  const arrivalPhoto = useMemo<DayStripPhoto | null>(() => (
    // Кадр прихода, а не фото на назначенное время (оно у опозданий в photo_url, #2604).
    (sa?.arrival_photo_url ?? sa?.photo_url) && (sa?.arrival_photo_at || sa?.photo_at || sa?.first_seen_at)
      ? { image_url: (sa!.arrival_photo_url ?? sa!.photo_url)!, at: (sa!.arrival_photo_at || sa!.photo_at || sa!.first_seen_at)!, metric: 'первые за баром', dip: true, group: 'staff-arrival' }
      : null
  ), [sa]);
  const withArrival = useCallback((list: DayStripPhoto[], dom?: [number, number]) => {
    if (!arrivalPhoto) return list;
    const t = new Date(arrivalPhoto.at).getTime();
    if (dom && (t < dom[0] || t > dom[1])) return list;
    if (list.some((p) => p.group === 'staff-arrival')) return list;
    return [...list, arrivalPhoto].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }, [arrivalPhoto]);
  const stripPhotos: DayStripPhoto[] = useMemo(() => withArrival(buildQueueStrip(photos)), [photos, withArrival]);
  // Зум на карточке (#1431/#1435): лента показывает кадры ОКНА, как в полном экране —
  // пока плотный набор окна грузится, остаётся дневной набор, отфильтрованный по окну.
  const [cardWindowPhotos, setCardWindowPhotos] = useState<DayStripPhoto[] | null>(null);
  const cardStripPhotos = useMemo(() => {
    if (!cardZoom) return stripPhotos;
    const base = cardWindowPhotos && cardWindowPhotos.length > 0 ? cardWindowPhotos : stripPhotos;
    return base.filter((p) => { const t0 = new Date(p.at).getTime(); return Number.isFinite(t0) && t0 >= cardZoom[0] && t0 <= cardZoom[1]; });
  }, [cardZoom, cardWindowPhotos, stripPhotos]);
  // Lightbox gallery = EXACTLY the photos the strip renders (reported via
  // onVisiblePhotos), so swiping never pages past what's on screen.
  const [lbPhotos, setLbPhotos] = useState<DayStripPhoto[]>([]);
  const srcs = lbPhotos.map((p) => p.image_url!).filter(Boolean);
  // Multi-day ranges: captions carry the day («пн 14:20»), not a bare clock.
  const rangeMode = toDay !== day;
  const captionTime = (iso: string) =>
    rangeMode
      ? new Date(iso).toLocaleString('ru-RU', { weekday: 'short', hour: '2-digit', minute: '2-digit' })
      : ft(iso);
  const captions = lbPhotos.map((p) => {
    const tlabel = captionTime(p.at);
    if (p.group === 'staff-arrival') return `Первые сотрудники за баром${tlabel ? ` · ${tlabel}` : ''}`;
    const split = queueSplitAt(p.at);
    return `Очередь${p.metric ? ` · ${p.metric}` : ''}${split ? ` (${split})` : ''}${tlabel ? ` · ${tlabel}` : ''}`;
  });

  // Custom legend: «Очередь» reads FIRST even though the bars are declared
  // last (declaration order = z-order, the bars must sit on top of the
  // lines), with click-to-toggle + dimming for hidden series.
  const LEGEND_ORDER: Record<string, number> = { queue: 0, cashier_count: 8, kitchen: 9 };
  queues.forEach((q, i) => {
    LEGEND_ORDER[`queue_${q.id}`] = 1 + i;
  });
  const renderLegend = (props: unknown) => {
    const payload =
      (props as { payload?: Array<{ value?: string; color?: string; dataKey?: unknown; type?: string }> })
        .payload ?? [];
    const items = [...payload].sort(
      (a, b) => (LEGEND_ORDER[String(a.dataKey)] ?? 9) - (LEGEND_ORDER[String(b.dataKey)] ?? 9),
    );
    return (
      <ul className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 pt-1 text-[11px]">
        {items.map((entry) => {
          const dk = String(entry.dataKey ?? '');
          const off = !!hiddenSeries[dk];
          return (
            // Pill-shaped TOGGLE. Both states stay fully readable (the hidden
            // one was opacity-40 and easy to miss — Alexey 2026-07-21); the
            // ACTIVE one is marked by a filled/bordered highlight instead.
            <li
              key={dk}
              onClick={() => dk && toggleSeries(dk)}
              className={cn(
                'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-2 py-0.5 transition-colors',
                off
                  ? 'border-transparent hover:bg-muted/40'
                  : 'border-border bg-muted/60 hover:bg-muted',
              )}
            >
              <span
                className="inline-block shrink-0"
                style={
                  dk.startsWith('queue')
                    ? { width: 10, height: 10, background: entry.color, borderRadius: 2 }
                    : { width: 12, height: 2.5, background: entry.color, borderRadius: 1 }
                }
              />
              <span style={{ color: entry.color }}>{entry.value}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  // Domain across midnight: midnights read «пн 13.10», the domain start
  // «13.10 22:00», the rest the clock; one day — the clock only. The old test
  // was «span > 2 days», so a 2-day pick showed bare HH:MM (client via Alexey
  // 13.10 #5801). Tooltips always carry the date.
  const multiDay = !!domain && spansDays(domain[0], domain[1]);
  const fmtTick = (ms: number) => fmtTimeTick(ms, domain);
  const fmtLabel = (ms: number) => fmtDayTime(ms);
  const fmtPill = (ms: number) => (multiDay ? fmtDayTime(ms) : fmtClock(ms));

  // The chart, parametrised by X domain + height so the in-card view and the
  // fullscreen lightbox share one renderer.
  // Bars are CENTRED on their bucket timestamp, so a domain pinned exactly to
  // the data cut the first/last bar in half — pad the rendered X domain by
  // half a bucket on each side. The strip + marquee pad identically
  // (axisPadMs), keeping photos glued to their chart x.
  const axisPadMs = bucketMs / 2;
  const renderChart = ({ domain: dom, height, fullscreen }: { domain: [number, number]; height: number; fullscreen?: boolean }) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={rowsFor(dom)}
        margin={{ top: 4, right: PLOT_INSET.right, bottom: 0, left: 0 }}
        // No focus ring / keyboard layer — clicking the chart drew a blue
        // outline around it (and its dots) on every click.
        accessibilityLayer={false}
        onClick={(state: { activeIndex?: number | string | null; activeLabel?: number | string | null }) => {
          // Fullscreen: the lightbox's marquee owns pointer gestures. recharts
          // fires this onClick on the marquee's release too, and in fullscreen
          // there's no clickGuard to suppress it, so it opened a photo and the
          // drag-to-zoom "did nothing" (Alexey 2026-07-22, 825a). The in-card
          // chart keeps click-to-open; fullscreen stays passive like the layout
          // card's separate fullscreen renderer.
          if (fullscreen) return;
          // A marquee ATTEMPT on the in-card chart (drag + release) must not
          // count as a click — only real taps open the nearest photo.
          if (clickGuard.current) return;
          // recharts v3: chart-onClick → activeIndex / activeLabel (no
          // activePayload). Resolve the clicked bucket's ISO and open the
          // nearest photo.
          const rows = rowsFor(dom);
          const idx = state?.activeIndex != null ? Number(state.activeIndex) : NaN;
          const row = Number.isFinite(idx) && idx >= 0 ? rows[idx] : undefined;
          const iso =
            row?.time ??
            (state?.activeLabel != null ? new Date(Number(state.activeLabel)).toISOString() : null);
          // Index into the RENDERED set (what the lightbox shows), not all photos.
          const photoIdx = nearestPhotoIndex(lbPhotos, iso);
          if (photoIdx >= 0) setOpenIdx(photoIdx);
        }}
        // Fullscreen: the crosshair (marquee-select) is the affordance; the
        // pointer cursor stays only on the in-card click-a-point chart.
        style={fullscreen ? undefined : { cursor: 'pointer' }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.4} />
        {sa && saTs && nobodyBeforeOpen && Number.isFinite(saTs.captureFrom) && Number.isFinite(saTs.open) && (
          <ReferenceArea yAxisId="people" x1={saTs.captureFrom} x2={saTs.open} fill="#f59e0b" fillOpacity={0.12} ifOverflow="hidden" />
        )}
        {sa && saTs && Number.isFinite(saTs.open) && (
          <ReferenceLine yAxisId="people" x={saTs.open} stroke="var(--color-muted-foreground)" strokeWidth={1.5}
            label={{ value: `открытие ${fmtTick(saTs.open)}`, position: 'insideTopLeft', fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
        )}
        {sa && saTs && Number.isFinite(saTs.expected) && (
          <ReferenceLine yAxisId="people" x={saTs.expected} stroke="#10b981" strokeDasharray="4 3" strokeWidth={1.5}
            label={{ value: `сотрудники к ${fmtTick(saTs.expected)}`, position: 'insideTopRight', fontSize: 10, fill: '#10b981' }} />
        )}
        {sa && saTs && Number.isFinite(saTs.first) && (
          <ReferenceLine yAxisId="people" segment={[{ x: saTs.first, y: 0 }, { x: saTs.first, y: Math.max(1, Math.min(yMax, 2)) }]} stroke="#10b981" strokeWidth={4} strokeLinecap="round"
            label={{ value: `первые за баром ${fmtTick(saTs.first)}`, position: 'top', fontSize: 10, fill: '#10b981' }} />
        )}
        {/* Порог очереди (CheckoutQueue.max_allowed_size; Алексей 21.10 #1671): выше линии —
            «очередь больше нормы» (красный на дашборде), на линии — жёлтый. */}
        {!hiddenSeries.queue && (data?.queue_limits ?? []).map((l) => (
          <ReferenceLine key={`ql${l.id}`} yAxisId="people" y={l.max_allowed} stroke="#dc2626" strokeOpacity={0.7}
            strokeDasharray="6 4" strokeWidth={1.5} ifOverflow="extendDomain"
            label={{ value: `порог ${l.max_allowed} чел.${(data?.queue_limits?.length ?? 0) > 1 ? ` · ${l.name}` : ''}`,
              position: 'insideTopRight', fontSize: 10, fill: '#dc2626' }} />
        ))}
        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={[dom[0] - axisPadMs, dom[1] + axisPadMs]}
          // Shared time axis (timeAxis.ts): the FIRST value (working-day
          // open / zoom start) is always labelled; ticks/labels follow the
          // RENDERED domain, so a fullscreen zoom across midnight gets
          // «пн 20.10» / «20.10 14:00» too, a one-day zoom the clock (#2646).
          ticks={makeTimeAxis(dom).ticks}
          tickFormatter={makeTimeAxis(dom).format}
          tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
          axisLine={false}
          tickLine={false}
          minTickGap={32}
          allowDataOverflow
        />
        <YAxis
          yAxisId="people"
          tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
          axisLine={false}
          tickLine={false}
          width={PLOT_INSET.left}
          allowDecimals={false}
          domain={[0, yMax]}
        />
        <Tooltip
            isAnimationActive={false}
          contentStyle={{
            fontSize: 12,
            borderRadius: 8,
            border: '1px solid var(--color-border)',
            background: 'var(--color-card)',
            color: 'var(--color-foreground)',
          }}
          labelFormatter={(l) => fmtLabel(Number(l))}
        />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} content={renderLegend} />
        {/* Accent lines drawn FIRST so they sit behind the bars. */}
        {hasCashier && (
          <Line
            yAxisId="people"
            type="stepAfter"
            dataKey="cashier_count"
            name={t('report.day.openCashiers')}
            stroke="#6366f1"
            strokeWidth={1.5}
            strokeOpacity={0.7}
            dot={false}
            isAnimationActive={false}
            hide={!!hiddenSeries.cashier_count}
          />
        )}
        {hasKitchen && (
          <Line
            yAxisId="people"
            type="stepAfter"
            dataKey="kitchen"
            name={t('report.day.kitchenStaff')}
            stroke="#10b981"
            strokeWidth={1.5}
            strokeOpacity={0.7}
            dot={false}
            isAnimationActive={false}
            hide={!!hiddenSeries.kitchen}
          />
        )}
        {multiQueue ? (
          // Two+ named queues: stacked shades of the queue orange, one per
          // queue (the tooltip and legend split accordingly).
          queues.map((q, i) => (
            <Bar
              key={q.id}
              yAxisId="people"
              dataKey={`queue_${q.id}`}
              name={q.name}
              stackId="queue"
              fill={QUEUE_SHADES[i % QUEUE_SHADES.length]}
              fillOpacity={0.85}
              radius={i === queues.length - 1 ? [2, 2, 0, 0] : undefined}
              isAnimationActive={false}
              cursor="pointer"
              hide={!!hiddenSeries[`queue_${q.id}`]}
            />
          ))
        ) : (
          <Bar
            yAxisId="people"
            dataKey="queue"
            name={t('report.day.queueLen')}
            fill="#f59e0b"
            fillOpacity={0.85}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
            cursor="pointer"
            hide={!!hiddenSeries.queue}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  // Load denser queue frames for the lightbox's window (full day or a zoom),
  // spread evenly across the period and sized to the lightbox's own width.
  const loadWindowQueuePhotos = useCallback(
    async (dom: [number, number], count: number): Promise<DayStripPhoto[]> => {
      const r = await getQueueDetail(zoneId, day, toDay, bucketMs, {
        // 'peak' (not 'even'): fullscreen must surface the BUSIEST moments, same
        // as the card — 'even' spread kept showing 0-people calm frames (Alexey).
        // Cap the count so peak selection's min-gap (span/count·2) stays wide
        // enough to pick genuinely separated peaks, not a near-uniform local-max
        // spread (825b) — the lightbox asks for 48, which collapses the gap.
        photoMode: 'peak',
        photoCount: Math.min(count, 16),
        fromTs: new Date(dom[0]).toISOString(),
        toTs: new Date(dom[1]).toISOString(),
      });
      return withArrival(buildQueueStrip(r.data.queue_photos ?? []), dom);
    },
    [zoneId, day, toDay, bucketMs, withArrival],
  );

  const cardReqRef = useRef(0);
  useEffect(() => {
    const id = ++cardReqRef.current;
    if (!cardZoom) { setCardWindowPhotos(null); return; }
    loadWindowQueuePhotos(cardZoom, 12)
      .then((ph) => { if (cardReqRef.current === id) setCardWindowPhotos(ph); })
      .catch(() => { if (cardReqRef.current === id) setCardWindowPhotos(null); });
    // Только границы окна — один запрос на выделение.
  }, [cardZoom?.[0], cardZoom?.[1]]);

  // NOT gated on isLoading — the previous day's chart stays on screen while a
  // revalidate runs (keepPreviousData), so the expand button never blinks out.
  const canExpand = overlay.length > 0 && !!domain;

  // Not configured / no queue polygons → render nothing. AFTER every hook —
  // an early return above a hook is the classic React #300.
  if (!isLoading && data && !data.configured) return null;
  if (!isLoading && data && overlay.length === 0 && photos.length === 0) return null;

  // Bar-width in time units = the 15-min bucket, so bars sit on the numeric axis.
  const chartH = compact ? 130 : CHART_H;

  return (
    <Card>
      <CardHeader className={cn('flex flex-row items-center justify-between gap-3', compact ? 'pt-2 pb-1' : 'pt-4 pb-2')}>
        <CardTitle className={CARD_TITLE}>{title ?? t('report.day.queueCardTitle')}</CardTitle>
        {cardZoom && !controlled && (
          // Пилюля подпериода заметная, крест рядом — сброс (Алексей 12.10, #1444); слов «вся смена» нет.
          <span className="ml-auto inline-flex h-7 items-center gap-1 rounded-full bg-primary/10 pl-3 pr-1 text-xs font-semibold tabular-nums text-primary ring-1 ring-primary/30">
            {fmtPill(cardZoom[0])}–{fmtPill(cardZoom[1])}
            <UiTooltip content="Сбросить период"><button type="button" onClick={() => { setCardZoom(null); setLbZoom(null); }} aria-label="Сбросить период"
              className="inline-flex size-5 items-center justify-center rounded-full hover:bg-primary/20">
              <X className="size-3" />
            </button></UiTooltip>
          </span>
        )}
        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t('report.day.expand')}
          >
            <Maximize2 className="size-4" />
          </button>
        )}
      </CardHeader>
      <CardContent className={compact ? 'space-y-1.5' : 'space-y-3'}>
        {!data && isLoading && <div className="h-72 animate-pulse rounded-xl bg-muted" />}

        {/* Images ABOVE the chart. */}
        <div>
          {cardStripPhotos.length > 0 && cardDomain && (
            <DayPhotoStrip
              photos={cardStripPhotos}
              workStart={new Date(cardDomain[0] - axisPadMs).toISOString()}
              workEnd={new Date(cardDomain[1] + axisPadMs).toISOString()}
              onOpen={setOpenIdx}
              onVisiblePhotos={setLbPhotos}
              slotW={QUEUE_SLOT_W}
              // Show ≥6 even for wide crops — count wins over aspect (847/848).
              minCount={6}
              // Mobile scrolls horizontally → bigger crops (owner: too small on
              // phones). Queue default imgH is 84; ×1.6 ≈ 134 on mobile.
              mobileScale={1.6}
              axisInset={{ left: PLOT_INSET.left, right: PLOT_INSET.right }}
            />
          )}
        </div>

        {overlay.length > 0 && domain && cardDomain && (
          <div
            onPointerDownCapture={(e) => {
              guardStart.current = { x: e.clientX, y: e.clientY };
              clickGuard.current = false;
            }}
            onPointerUpCapture={(e) => {
              const s = guardStart.current;
              clickGuard.current =
                !!s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 8;
            }}
          >
            <MarqueeZoom
              // Плоскость графика — ПАДДИНГОВЫЙ домен (axisPadMs), как у полного экрана.
              domain={[cardDomain[0] - axisPadMs, cardDomain[1] + axisPadMs]}
              plotInset={PLOT_INSET}
              onZoom={(w) => {
                const lo = Math.max(w[0], domain[0]); const hi = Math.min(w[1], domain[1]);
                if (hi - lo < 5 * 60_000) return;
                if (controlled) { onZoom!([lo, hi]); return; }
                setCardZoom([lo, hi]);
                setLbZoom([lo, hi]);
              }}
              onReset={() => { if (controlled) { onZoom!(null); return; } setCardZoom(null); setLbZoom(null); }}
            >
              {renderChart({ domain: cardDomain, height: chartH })}
            </MarqueeZoom>
          </div>
        )}

        {openIdx != null && (
          <ImageLightbox
            srcs={srcs}
            index={openIdx}
            alt=""
            captions={captions}
            onClose={() => setOpenIdx(null)}
            onNavigate={setOpenIdx}
          />
        )}
      </CardContent>

      {expanded && domain && (
        <DayChartLightbox
          title={title ?? t('report.day.queueCardTitle')}
          photos={stripPhotos}
          captionOf={(p) => {
            const tlabel = ft(p.at);
            if (p.group === 'staff-arrival') return `Первые сотрудники за баром${tlabel ? ` · ${tlabel}` : ''}`;
            return `Очередь${p.metric ? ` · ${p.metric}` : ''}${tlabel ? ` · ${tlabel}` : ''}`;
          }}
          fullDomain={domain}
          initialZoom={presetZoom ? clampDomain(presetZoom, domain) : null}
          slotW={QUEUE_SLOT_W}
          plotInset={PLOT_INSET}
          axisPadMs={axisPadMs}
          renderChart={renderChart}
          // Fullscreen = MORE photos (2 staggered rows), same rule as Столы.
          stripLanes={2}
          loadWindowPhotos={loadWindowQueuePhotos}
          onZoomChange={setLbZoom}
          onClose={() => {
            setExpanded(false);
            setPresetZoom(null);
            setLbZoom(cardZoom);
          }}
        />
      )}
    </Card>
  );
}
